import Box from "@mui/material/Box";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Fragment, useMemo, useState } from "react";
import type { ImportSourceHealth } from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import ScopeToggle from "../../filters/ScopeToggle.tsx";
import { StatusLabel } from "../../StatusIcon.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";
import { NEEDS_ATTENTION, importStatus } from "./importStats.ts";

type Scope = "all" | "attention";

/**
 * Every import the platform runs, grouped by kind: is it current, when did it
 * last succeed, what did its last run bring in and how much it holds.
 */
export default function SourceHealthPanel({ sources }: { sources: ImportSourceHealth[] }) {
  const attention = sources.filter((s) => NEEDS_ATTENTION.includes(s.status));
  const [scope, setScope] = useState<Scope>(attention.length ? "attention" : "all");
  const shown = scope === "attention" ? attention : sources;
  const groups = useMemo(() => {
    const byGroup = new Map<string, ImportSourceHealth[]>();
    for (const s of shown) byGroup.set(s.group, [...(byGroup.get(s.group) ?? []), s]);
    return [...byGroup.entries()];
  }, [shown]);

  return (
    <Panel
      title="Imports"
      subtitle="Each source's last successful run and what it holds"
      count={sources.length}
      disablePadding
      action={
        <ScopeToggle<Scope>
          value={scope}
          onChange={setScope}
          aria-label="Which imports"
          options={[
            { value: "attention", label: "Needs attention", badge: attention.length },
            { value: "all", label: "All" },
          ]}
        />
      }
    >
      {shown.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", px: 2, py: 3, textAlign: "center" }}>
          Every import is up to date
        </Typography>
      ) : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last success</TableCell>
              <TableCell align="right">Last run</TableCell>
              <TableCell align="right">Records</TableCell>
              <TableCell align="right">Last 24h</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(([group, rows]) => (
              <Fragment key={group}>
                <TableRow>
                  <TableCell colSpan={6} sx={{ bgcolor: "action.hover", py: 0.75 }}>
                    <Typography variant="overline" sx={{ color: "text.secondary" }}>
                      {group}
                    </Typography>
                  </TableCell>
                </TableRow>
                {rows.map((s) => (
                  <SourceRow key={s.key} source={s} />
                ))}
              </Fragment>
            ))}
          </TableBody>
        </ScrollTable>
      )}
    </Panel>
  );
}

function SourceRow({ source: s }: { source: ImportSourceHealth }) {
  const status = importStatus(s.status);
  const why = s.last_error ?? s.detail;
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {s.label}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {s.schedule}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={why ?? ""} disableHoverListener={!why}>
          <Box component="span">
            <StatusLabel kind={status.kind} label={status.label} />
          </Box>
        </Tooltip>
        {s.failures_7d > 0 && (
          <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
            {s.failures_7d === 1 ? "1 failed run" : `${formatNumber(s.failures_7d)} failed runs`} this week
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <RelativeTime value={s.last_success_at} />
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {s.last_run_records != null ? formatNumber(s.last_run_records) : "–"}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {s.total != null ? formatNumber(s.total) : "–"}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
        {s.added_24h ? `+${formatNumber(s.added_24h)}` : s.refreshed_24h ? `${formatNumber(s.refreshed_24h)} refreshed` : "–"}
      </TableCell>
    </TableRow>
  );
}
